/** Who is signed in, what language they read, and which ground they are on. */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { api, ApiError } from './api';
import type { User } from './api';
import { dictionaries } from './i18n';
import type { Locale } from './i18n';

type Theme = 'dark' | 'light';

interface Session {
  user: User | null;
  loading: boolean;
  locale: Locale;
  t: (typeof dictionaries)['en'];
  theme: Theme;
  setTheme: (t: Theme) => void;
  setUser: (u: User | null) => void;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

// Light unless somebody asked for dark. The inline script in index.html has
// already stamped the stored choice on <html> before first paint; this only
// reads back what it decided.
function storedTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [theme, setThemeState] = useState<Theme>(storedTheme);

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.me();
      setUser(user);
    } catch (err) {
      // 401 is the ordinary answer for a visitor who is not signed in.
      if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    document.documentElement.dataset.theme = next;
    try { localStorage.setItem('omibee-theme', next); } catch { /* private window */ }
  }, []);

  // The account decides the language; the stored copy only keeps the sign-in
  // page from flashing the wrong words before we know who is asking.
  const locale: Locale = user?.locale ?? readLocale();
  useEffect(() => {
    document.documentElement.lang = locale;
    try { localStorage.setItem('omibee-locale', locale); } catch { /* private window */ }
  }, [locale]);

  const value = useMemo<Session>(() => ({
    user, loading, locale, t: dictionaries[locale], theme, setTheme, setUser, refresh,
  }), [user, loading, locale, theme, setTheme, refresh]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

function readLocale(): Locale {
  try {
    const stored = localStorage.getItem('omibee-locale');
    if (stored === 'pt' || stored === 'en') return stored;
  } catch { /* private window */ }
  return navigator.language.toLowerCase().startsWith('pt') ? 'pt' : 'en';
}

export function useSession(): Session {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession outside a SessionProvider');
  return value;
}

/** useSignedIn is for the screens that cannot render without an account. */
export function useSignedIn(): Session & { user: User } {
  const session = useSession();
  if (!session.user) throw new Error('useSignedIn without a session');
  return session as Session & { user: User };
}
